import jsPDF from "jspdf";
import { toPng } from "html-to-image";

// Fonts actually used by label text (loaded from Google Fonts, which serves
// proper CORS headers). Passing this explicitly as `fontEmbedCss` skips
// html-to-image's automatic font-discovery scan, which otherwise iterates
// every <link> stylesheet on the page - including unrelated third-party CDN
// CSS (Bootstrap, Prism, Dropzone, etc.) that has no CORS headers and throws
// harmless but noisy SecurityErrors when it tries to read their cssRules.
const LABEL_FONT_EMBED_CSS = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&family=Roboto&family=Noto+Sans:wght@400;600;700&family=Noto+Sans+Devanagari:wght@400;600;700&family=Mukta:wght@400;600;700&family=Hind:wght@400;600;700&display=swap');`;

// Renders the given DOM node (a LabelCanvas root) to a high resolution PNG data URL.
// Rasterizing the DOM (instead of drawing vector text with jsPDF) guarantees Marathi
// Unicode / Devanagari text renders correctly in the exported PDF, since the browser
// itself paints the text using the loaded Noto Sans Devanagari / Google Fonts.
async function captureNodeAsPng(node, pixelRatio = 4) {
  return toPng(node, { pixelRatio, cacheBust: true, fontEmbedCSS: LABEL_FONT_EMBED_CSS });
}

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
// Extra slack subtracted from the usable page area so rows/columns never sit
// exactly on the page edge - float rounding or a printer's own hardware
// margin could otherwise clip the last row/column instead of pushing it to
// the next page.
const SAFE_BUFFER_MM = 1;

// Works out how many label-sized stickers fit on one A4 sheet, and the
// column/row grid needed to tile `copies` of them (used by both the PDF
// export and the browser print path so the layouts stay in sync).
export function computeA4Grid(
  widthMm,
  heightMm,
  { marginXMm = 5, marginYMm = 5, spacingXMm = 2, spacingYMm = 2 } = {},
) {
  // A label as large as (or larger than) the page itself just gets one per page.
  if (widthMm >= A4_WIDTH_MM || heightMm >= A4_HEIGHT_MM) {
    return { columns: 1, rows: 1, perPage: 1, marginXMm: 0, marginYMm: 0, spacingXMm, spacingYMm };
  }
  const columns = Math.max(
    1,
    Math.floor(
      (A4_WIDTH_MM - 2 * marginXMm - SAFE_BUFFER_MM + spacingXMm) / (widthMm + spacingXMm),
    ),
  );
  const rows = Math.max(
    1,
    Math.floor(
      (A4_HEIGHT_MM - 2 * marginYMm - SAFE_BUFFER_MM + spacingYMm) / (heightMm + spacingYMm),
    ),
  );
  return { columns, rows, perPage: columns * rows, marginXMm, marginYMm, spacingXMm, spacingYMm };
}

// Resolves the grid to actually use for tiling: starts from the auto-fit
// grid, then applies any explicit overrides (e.g. a template's configured
// a4Columns/a4Rows) but clamps them so they can never exceed what physically
// fits on the page - otherwise an explicit but too-ambitious grid would
// place a row/column partly off the page, cutting it instead of flowing it
// to the next page.
function resolveA4Grid(widthMm, heightMm, gridOptions = {}) {
  const autoFit = computeA4Grid(widthMm, heightMm, gridOptions);
  const columns = gridOptions.columns
    ? Math.min(gridOptions.columns, autoFit.columns)
    : autoFit.columns;
  const rows = gridOptions.rows ? Math.min(gridOptions.rows, autoFit.rows) : autoFit.rows;
  return {
    columns,
    rows,
    perPage: columns * rows,
    marginXMm: autoFit.marginXMm,
    marginYMm: autoFit.marginYMm,
    spacingXMm: autoFit.spacingXMm,
    spacingYMm: autoFit.spacingYMm,
  };
}


// Export a single label as a PDF sized exactly to the label's physical dimensions (mm).
export async function exportSingleLabelPdf(node, widthMm, heightMm, fileName = "label.pdf") {
  const dataUrl = await captureNodeAsPng(node);
  const orientation = widthMm >= heightMm ? "landscape" : "portrait";
  const doc = new jsPDF({ orientation, unit: "mm", format: [widthMm, heightMm] });
  doc.addImage(dataUrl, "PNG", 0, 0, widthMm, heightMm);
  doc.save(fileName);
  return doc;
}

// Export multiple identical copies of a label tiled on an A4 sheet, using the
// template's configured columns/rows/margins/spacing.
export async function exportA4SheetPdf(
  node,
  widthMm,
  heightMm,
  { columns = 2, rows = 5, marginXMm = 5, marginYMm = 5, spacingXMm = 2, spacingYMm = 2 } = {},
  fileName = "labels-a4-sheet.pdf"
) {
  const dataUrl = await captureNodeAsPng(node);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = 210;
  const pageHeight = 297;

  const totalWidth = columns * widthMm + (columns - 1) * spacingXMm;
  const totalHeight = rows * heightMm + (rows - 1) * spacingYMm;
  const startX = Math.max(marginXMm, (pageWidth - totalWidth) / 2);
  const startY = Math.max(marginYMm, (pageHeight - totalHeight) / 2);

  let placed = 0;
  const perPage = columns * rows;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < columns; c++) {
      const x = startX + c * (widthMm + spacingXMm);
      const y = startY + r * (heightMm + spacingYMm);
      if (y + heightMm > pageHeight - marginYMm) continue;
      doc.addImage(dataUrl, "PNG", x, y, widthMm, heightMm);
      placed++;
      if (placed % perPage === 0 && placed < rows * columns) {
        doc.addPage();
      }
    }
  }

  doc.save(fileName);
  return doc;
}

// Export N copies (not necessarily filling a full A4 grid) onto as many A4 pages as needed.
export async function exportCopiesAsA4Pdf(
  node,
  widthMm,
  heightMm,
  copies,
  gridOptions = {},
  fileName = "labels.pdf"
) {
  const doc = await buildA4LabelsPdf(node, widthMm, heightMm, copies, gridOptions);
  doc.save(fileName);
  return doc;
}

// Builds a multi-page jsPDF document tiling `copies` labels across A4 pages.
// Shared by exportCopiesAsA4Pdf and printCopiesOnA4 so the PDF export and the
// direct-print path always produce identical, correctly paginated layouts -
// generating a real PDF avoids the pagination quirks of printing live HTML
// (CSS page breaks can clip or skip pages depending on browser/driver).
async function buildA4LabelsPdf(node, widthMm, heightMm, copies, gridOptions = {}) {
  const dataUrl = await captureNodeAsPng(node);
  const { columns, rows, marginXMm, marginYMm, spacingXMm, spacingYMm } = resolveA4Grid(
    widthMm,
    heightMm,
    gridOptions,
  );
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = A4_WIDTH_MM;
  const pageHeight = A4_HEIGHT_MM;

  const totalWidth = columns * widthMm + (columns - 1) * spacingXMm;
  const totalHeight = rows * heightMm + (rows - 1) * spacingYMm;
  const startX = Math.max(marginXMm, (pageWidth - totalWidth) / 2);
  const startY = Math.max(marginYMm, (pageHeight - totalHeight) / 2);

  let remaining = copies;
  let firstPage = true;

  while (remaining > 0) {
    if (!firstPage) doc.addPage();
    firstPage = false;
    outer: for (let r = 0; r < rows; r++) {
      for (let c = 0; c < columns; c++) {
        if (remaining <= 0) break outer;
        const x = startX + c * (widthMm + spacingXMm);
        const y = startY + r * (heightMm + spacingYMm);
        doc.addImage(dataUrl, "PNG", x, y, widthMm, heightMm);
        remaining--;
      }
    }
  }

  return doc;
}

// Opens the browser print dialog with just the rasterized label image.
export async function printLabelNode(node, widthMm, heightMm) {
  const dataUrl = await captureNodeAsPng(node);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`
    <html>
      <head>
        <style>
          @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
          html, body { margin: 0; padding: 0; }
          img { width: ${widthMm}mm; height: ${heightMm}mm; display: block; }
        </style>
      </head>
      <body>
        <img src="${dataUrl}" />
      </body>
    </html>
  `);
  doc.close();

  return new Promise((resolve) => {
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        resolve();
      }, 1000);
    };
  });
}

// Opens the browser print dialog with `copies` labels tiled across as many
// A4 pages as needed, using the same grid math as exportCopiesAsA4Pdf so the
// layout and pagination match. Unlike the PDF export path, this prints plain
// HTML/CSS directly (like printLabelNode) rather than embedding a generated
// PDF in an iframe - embedding a PDF depends on the browser's built-in PDF
// viewer (a browser extension), which can be disabled or blocked by ad/content
// blockers, causing the print dialog to silently open with a blank/broken
// page. Positioning plain <img> tags with @page/page-break CSS has no such
// dependency and matches how single-label printing already works reliably.
export async function printCopiesOnA4(node, widthMm, heightMm, copies, gridOptions = {}) {
  const dataUrl = await captureNodeAsPng(node);
  const { columns, rows, marginXMm, marginYMm, spacingXMm, spacingYMm } = resolveA4Grid(
    widthMm,
    heightMm,
    gridOptions,
  );
  const perPage = columns * rows;
  const pageCount = Math.max(1, Math.ceil(copies / perPage));

  let remaining = copies;
  let pagesHtml = "";
  for (let p = 0; p < pageCount; p++) {
    let imagesHtml = "";
    for (let i = 0; i < perPage && remaining > 0; i++) {
      const r = Math.floor(i / columns);
      const c = i % columns;
      const x = marginXMm + c * (widthMm + spacingXMm);
      const y = marginYMm + r * (heightMm + spacingYMm);
      imagesHtml += `<img src="${dataUrl}" style="position:absolute; left:${x}mm; top:${y}mm; width:${widthMm}mm; height:${heightMm}mm;" />`;
      remaining--;
    }
    pagesHtml += `<div class="a4-page">${imagesHtml}</div>`;
  }

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`
    <html>
      <head>
        <style>
          @page { size: A4; margin: 0; }
          html, body { margin: 0; padding: 0; }
          .a4-page {
            position: relative;
            width: ${A4_WIDTH_MM}mm;
            height: ${A4_HEIGHT_MM}mm;
            page-break-after: always;
          }
          .a4-page:last-child { page-break-after: auto; }
        </style>
      </head>
      <body>${pagesHtml}</body>
    </html>
  `);
  doc.close();

  return new Promise((resolve) => {
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        resolve();
      }, 1000);
    };
  });
}
